import { Component, DestroyRef, ElementRef, inject, input, OnInit, signal, ViewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { BillingService } from '@app/modules/payments/services/billing.service'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { environment } from '@environments/environment'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { firstValueFrom } from 'rxjs'
import { filter } from 'rxjs/operators'
import { OrganizationService } from '../../../../services/organization.service'

declare let Stripe: any

@Component({
  selector: 'app-billing-add-payment-card-modal',
  standalone: true,
  imports: [ModalComponent, TranslateModule, NgxSkeletonLoaderModule],
  templateUrl: './add-payment-card-modal.component.html',
  styleUrl: './add-payment-card-modal.component.scss'
})
export class AddPaymentCardModalComponent implements OnInit {
  public readonly modalId = input.required<string>()
  public readonly headerIcon = input.required<string>()
  /** When true, the "set as default" checkbox starts checked (e.g. first card). */
  public readonly setAsDefaultByDefault = input(false)

  @ViewChild('cardElement') cardElementRef?: ElementRef<HTMLElement>

  public isInitializing = signal(false)
  public isSaving = signal(false)
  public setAsDefault = signal(false)
  public cardError = signal('')

  private readonly _modalService = inject(ModalService)
  private readonly _billingService = inject(BillingService)
  private readonly _organizationService = inject(OrganizationService)
  private readonly _translateService = inject(TranslateService)
  private readonly _destroyRef = inject(DestroyRef)

  private _stripe: any = null
  private _card: any = null
  private _clientSecret: string | null = null

  private readonly _cardStyle = {
    base: {
      color: '#374151',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '14px',
      '::placeholder': {
        color: '#9CA3AF'
      }
    },
    invalid: {
      color: '#DC2626'
    }
  }

  /**
   * Subscribe to modal open/close and initialize or teardown the Stripe form.
   */
  ngOnInit(): void {
    const modalId = this.modalId()

    this._modalService.modalState$
      .pipe(
        filter((state) => !!modalId && modalId in state),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((state) => {
        if (state[modalId]) {
          this._initializeForm()
        } else {
          this._teardownForm()
        }
      })
  }

  /**
   * Close the add payment card modal.
   */
  public close(): void {
    if (this._modalService.isModalOpen(this.modalId())) {
      this._modalService.toggleModal(this.modalId())
    }
  }

  /**
   * Toggle the "set as default" checkbox state.
   * @param {boolean} checked Checkbox state
   */
  public toggleSetAsDefault(checked: boolean): void {
    if (this.setAsDefaultByDefault()) return

    this.setAsDefault.set(checked)
  }

  /**
   * Whether the set-as-default checkbox is locked (e.g. first card must be default).
   * @returns {boolean} True when the checkbox should be checked and disabled
   */
  public isSetAsDefaultLocked(): boolean {
    return this.setAsDefaultByDefault()
  }

  /**
   * Gets skeleton theme colors based on current theme.
   */
  public getSkeletonTheme(): { backgroundColor: string; color: string } {
    return this._organizationService.getSkeletonTheme()
  }

  /**
   * Confirm the Stripe setup intent and save the new payment method.
   */
  public save(): void {
    if (this.isSaving() || this.isInitializing() || !this._stripe || !this._card || !this._clientSecret) {
      return
    }

    this.isSaving.set(true)
    this.cardError.set('')

    this._stripe
      .confirmCardSetup(this._clientSecret, {
        payment_method: {
          card: this._card
        }
      })
      .then((result: any) => {
        if (result.error) {
          this.cardError.set(result.error.message ?? this._translateService.instant('v2.orgSettings.billing.addPaymentCardSaveError'))
          this.isSaving.set(false)
          return
        }

        const paymentMethodId = typeof result.setupIntent?.payment_method === 'string' ? result.setupIntent.payment_method : result.setupIntent?.payment_method?.id

        if (!paymentMethodId) {
          this.cardError.set(this._translateService.instant('v2.orgSettings.billing.addPaymentCardSaveError'))
          this.isSaving.set(false)
          return
        }

        if (this.setAsDefault()) {
          this._organizationService
            .setDefaultPaymentMethod(this._billingService.workspaceId, paymentMethodId)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
              next: () => this._finishSave(),
              error: () => this._finishSave()
            })
          return
        }

        this._finishSave()
      })
      .catch(() => {
        this.cardError.set(this._translateService.instant('v2.orgSettings.billing.addPaymentCardSaveError'))
        this.isSaving.set(false)
      })
  }

  /**
   * Reload billing data and close the modal after a successful save.
   */
  private _finishSave(): void {
    this._billingService.reloadBillingDetails$.next(true)
    this.isSaving.set(false)
    this.close()
  }

  /**
   * Load Stripe.js, create a setup intent, and mount the card element.
   */
  private _initializeForm(): void {
    this._teardownForm()
    this.isInitializing.set(true)
    this.cardError.set('')
    this.setAsDefault.set(this.setAsDefaultByDefault())

    this._loadStripeScript()
      .then(async () => {
        this._stripe = Stripe(environment.stripeKey)
        const result = await firstValueFrom(this._organizationService.createPaymentMethodSetupIntent(this._billingService.workspaceId))
        this._clientSecret = result?.client_secret ?? null

        if (!this._clientSecret) {
          throw new Error('Missing setup intent client secret')
        }

        this.isInitializing.set(false)
        setTimeout(() => this._mountCardElement(), 0)
      })
      .catch(() => {
        this.cardError.set(this._translateService.instant('v2.orgSettings.billing.addPaymentCardLoadError'))
        this.isInitializing.set(false)
      })
  }

  /**
   * Mount the Stripe card element into the modal container.
   */
  private _mountCardElement(): void {
    const container = this.cardElementRef?.nativeElement
    if (!container || !this._stripe) return

    const elements = this._stripe.elements()
    this._card = elements.create('card', { style: this._cardStyle })
    this._card.mount(container)
    this._card.addEventListener('change', (event: any) => {
      this.cardError.set(event.error?.message ?? '')
    })
  }

  /**
   * Unmount Stripe elements and reset local form state.
   */
  private _teardownForm(): void {
    if (this._card) {
      this._card.unmount()
      this._card = null
    }

    this._clientSecret = null
    this.isInitializing.set(false)
    this.isSaving.set(false)
    this.cardError.set('')
    this.setAsDefault.set(false)
  }

  /**
   * Ensure Stripe.js is available before initializing Elements.
   */
  private _loadStripeScript(): Promise<void> {
    if (typeof Stripe !== 'undefined') {
      return Promise.resolve()
    }

    const existingScript = document.querySelector('script[data-payment="stripe"]')
    if (existingScript) {
      return new Promise((resolve) => {
        existingScript.addEventListener('load', () => resolve(), { once: true })
        if (typeof Stripe !== 'undefined') resolve()
      })
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.setAttribute('src', 'https://js.stripe.com/v3/')
      script.dataset['payment'] = 'stripe'
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Failed to load Stripe.js'))
      document.head.appendChild(script)
    })
  }
}
